import React from 'react';
import { Card, Col, Row, Spin, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import customerLifecycleService from '@/services/customerLifecycle.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { OiPageFrame } from '@/components/owner-intelligence/OwnerIntelligenceComponents';

const { Title } = Typography;

const SalesPipelineIntelligencePage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const { data, isLoading, error } = useQuery({
    queryKey: ['oi-sales-pipeline'],
    queryFn: () => customerLifecycleService.getSalesPipelineIntelligence(),
  });

  const leadGen = data?.lead_generation as Record<string, number> | undefined;
  const pipeline = data?.pipeline as Record<string, number> | undefined;
  const funnel = (data?.conversion_funnel as { stage: string; count: number }[]) ?? [];
  const byIndustry = (data?.pipeline_by_industry as { industry: string; count: number; value: number }[]) ?? [];

  return (
    <OiPageFrame
      subtitle="Presales pipeline, qualification ratios, and conversion funnel."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Sales pipeline intelligence unavailable"
    >
      <Title level={3}>Sales Pipeline Intelligence</Title>
      <Spin spinning={isLoading}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>Leads (12m): {leadGen?.total_leads ?? 0}</Card>
          </Col>
          <Col span={6}>
            <Card>Qualification ratio: {leadGen?.qualification_ratio_pct ?? 0}%</Card>
          </Col>
          <Col span={6}>
            <Card>Pipeline: {formatCurrency(pipeline?.pipeline_value ?? 0)}</Card>
          </Col>
          <Col span={6}>
            <Card>Weighted: {formatCurrency(pipeline?.probability_weighted_value ?? 0)}</Card>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Card title="Conversion funnel">
              <Table size="small" rowKey="stage" pagination={false} dataSource={funnel} columns={[
                { title: 'Stage', dataIndex: 'stage' },
                { title: 'Count', dataIndex: 'count' },
              ]} />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Pipeline by industry">
              <Table size="small" rowKey="industry" pagination={false} dataSource={byIndustry} columns={[
                { title: 'Industry', dataIndex: 'industry' },
                { title: 'Opps', dataIndex: 'count' },
                { title: 'Value', dataIndex: 'value', render: (v: number) => formatCurrency(v) },
              ]} />
            </Card>
          </Col>
        </Row>
      </Spin>
    </OiPageFrame>
  );
};

export default SalesPipelineIntelligencePage;
